import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import AboutSideNav from '../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../utils/seo';

// ===== 「協会について」セクション入口ハブ（Implementer B スコープ／2ファイル: about/page.tsx・en/about/page.tsx のみ） =====
// 役割: /about は下層6ページ（overview/mission/history/organization/activities/access）への入口ハブ。
//   単なるリンク集にせず、厚い導入（協会の顔）＋6サブページへのエディトリアル導線で構成する。
// デザイン土台: src/pages/about/overview/page.tsx（型の正典）＋ src/pages/home/page.tsx。
//   紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / リード3行+ / BTN-08 2状態 / jba-display / 装飾ディバイダー無し。
//   overview の単純コピーは禁止 → セクション型は本ページ内で全て別型。ディレクトリ型は新バリエーション（由来注記）。
// 事実正典（創作禁止・正典に無い年号/数値は書かない・出典path記録）:
//   [S1] mng-thebutler/00_事業説明.md — L22（コンセプト融合）/ L25-27（3つの提供価値）/ L34（1890年創立）/
//        L39（お台場・青海の所在地。※ビル名表記はサイト全体で不掲載=Yuichi 2026-07-17。住所は青海2丁目7−4まで）/ L61（130年以上・日本唯一の執事専門機関）
//   [S2] src/pages/about/overview/page.tsx（working tree・現行正典）— リードL97 / 本文L122-129 /
//        事業領域businessFields=5つ / 「135年」「3代」「東京・お台場」
//   [S3] src/pages/about/access/page.tsx / src/pages/home/page.tsx（導線先URL・料金/資格の実文）

export default function AboutPage() {
  useEffect(() => {
    updateSEO(seoData['/about']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について' }
  ];

  // サイドナビ（共通コンポーネント）。ハブ自身を先頭に置き現在ページとして強調。
  const aboutNav = [
    { label: '協会について', href: '/about' },
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  // 入口ディレクトリ（6サブページ）。ページ名は22px級serifで大きく（#6 一覧可読性）。
  const sections = [
    { no: '01', en: 'Overview', ja: '協会概要', href: '/about/overview', desc: '協会の成り立ちと組織の全体像。法人概要と5つの事業領域を一望できます。' },
    { no: '02', en: 'Mission', ja: '理念と使命', href: '/about/mission', desc: '日本のおもてなしと西洋式執事の融合という理念、私たちが大切にする提供価値をご紹介します。' },
    { no: '03', en: 'History', ja: '沿革・歴史', href: '/about/history', desc: '1890年（明治23年）の創立から現在まで。三代にわたる継承と歩みをたどります。' },
    { no: '04', en: 'Organization', ja: '組織体制', href: '/about/organization', desc: '頭取を中心とした協会の組織構成と、品質と信頼を支える体制をご説明します。' },
    { no: '05', en: 'Activities', ja: '主な活動', href: '/about/activities', desc: '人材育成・資格認定・セミナー・国際交流・情報発信という、協会の活動の柱をご紹介します。' },
    { no: '06', en: 'Access', ja: 'アクセス・所在地', href: '/about/access', desc: 'お台場・青海に構える本部の所在地と、最寄り駅からのアクセスをご案内します。' },
  ];

  // 私たちが大切にすること（3つの提供価値・出典 [S1] L25-27）
  const principles = [
    { key: 'i.', title: '時間と心の余裕', text: '段取りや雑事から解放し、仕事・家族・休息という本来向き合うべきことに集中できる環境そのものをお届けします。' },
    { key: 'ii.', title: '家の心臓として', text: '暮らしの最も内側に入り、生活の基盤を静かに整え、一族の機微に寄り添う「家の心臓」となることを目指します。' },
    { key: 'iii.', title: '見えない奉仕', text: '主人が言葉にする前にニーズを察知し、空気のように自然に動く「先回り」の奉仕を、執事の本質と考えます。' },
  ];

  // BTN-08（overview / home と同一文法・紺地=真鍮塗り白太字→hover紺塗り生成り）
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  return (
    <Layout>
      {/* SEO は updateSEO(seoData['/about']) を単一の正とする（JSX 二重定義は除去・H3） */}
      {/* ===== ヒーロー — 全幅ビルボード（画像+紺オーバーレイ+左寄せ）。
           由来: home / overview のヒーロー文法（全幅ビルボード・金eyebrow・真鍮短罫・左寄せリード）を入口ハブへ適用。===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/salon0926/1786973212294.jpg"
          alt="暖炉を背景にした執事サロンの銀のティーセット"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">About the Association</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事文化を未来へつなぐ、</span>
              <span className="block">私たちの営み</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の普及と発展に努めてきました。おもてなしの精神と西洋式執事の伝統を融合させ、世界に通用するプロフェッショナルな執事を育てています。このページでは、協会の姿を6つの切り口からご紹介します。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム（overviewと同じ section 骨格・共通ナビ配線） ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            {/* サイドナビ（共通コンポーネント・ハブ自身を強調） */}
            <AboutSideNav items={aboutNav} activeHref="/about" />

            {/* コンテンツ列 */}
            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* --- I. 導入（協会の顔）: 金eyebrow + 金差し色見出し + 4段落リード + 添え画像の2カラム。
                   由来: home ABOUT-F / overview S1 のリード文法を、入口ハブの導入プロース＋添え画像へ再構成。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Who We Are</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  日本で唯一の、<span className="text-[#a38e65]">執事専門機関</span>
                </h2>
                <div className="grid lg:grid-cols-[1fr_300px] gap-8 lg:gap-12 items-start">
                  <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                    <p>
                      全日本執事協会は、130年以上の系譜を持つ、日本で唯一の執事専門機関です。1890年（明治23年）の創立以来、日本における執事文化の普及と発展に努めてきました。
                    </p>
                    <p>
                      私たちが掲げるのは「日本のおもてなし精神と西洋式執事（バトラー）サービスの融合」です。英国式の規律と品格、そして日本独自の細やかな礼節を兼ね備えたプロフェッショナルが、お客様のプライベートからビジネスまでを包括的に支えます。
                    </p>
                    <p>
                      このページは、協会を知るための入口です。組織の全体像、理念、沿革、体制、活動、そして所在地まで——6つの切り口から、私たちの姿をご覧いただけます。
                    </p>
                    <p>
                      それぞれのページで、執事文化を次の時代へつなぐ協会の営みを、より深くお伝えします。まずは関心のある切り口からお進みください。
                    </p>
                  </div>
                  <div className="lg:pt-1">
                    <img
                      src="/images/generated/jba-fs-020-about-v2.webp"
                      alt="協会が受け継ぐ執事文化の理念を象徴する、静謐な応接の情景"
                      className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                </div>
              </section>

              {/* --- II. 入口ディレクトリ（6サブページ誘導）= 新バリエーション①
                   由来注記: home IV「全幅エディトリアル行型」（1行1項目・列整列・価格/矢印縦ライン）と NavyBand の
                   金ローマ番号文法を掛け合わせ、「セクション入口ディレクトリ」へ翻案。overview に下層ページ一覧型は
                   存在しないため新造。カード乱立を避け、ページ名を22px級serifで大きく細罫整列（#6 一覧可読性の指摘対応）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Explore</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  協会について、<span className="text-[#a38e65]">6つの切り口</span>から
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  協会を知る入口を、目的に合わせて6つのページに整理しました。全体像を先に掴みたい方は「協会概要」から、私たちの考え方に触れたい方は「理念と使命」から。気になる切り口を選んで、それぞれのページへお進みください。
                </p>
                <div className="border-t-2 border-[#1a2942]">
                  {sections.map((s) => (
                    <a
                      key={s.href}
                      href={s.href}
                      className="group flex items-center gap-5 sm:gap-8 py-6 sm:py-7 border-b border-[#e2ddd2] hover:bg-[#f8f6f0]/60 transition-colors duration-300"
                    >
                      {/* 番号（金Playfair・大） */}
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl sm:text-4xl leading-none shrink-0 w-12 sm:w-14">{s.no}</span>
                      {/* 英字イタリック + ページ名（22px級serif大）+ 説明 */}
                      <div className="min-w-0 flex-1">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{s.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-[22px] sm:text-2xl leading-[1.4] tracking-[0.02em] mb-1.5 group-hover:text-[#8f7a56] transition-colors">{s.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{s.desc}</p>
                      </div>
                      {/* 矢印（右端・縦ライン整列） */}
                      <span className="text-[#a38e65] text-xl sm:text-2xl shrink-0 w-6 text-right group-hover:translate-x-1 transition-transform">→</span>
                    </a>
                  ))}
                </div>
              </section>

              {/* --- III. 私たちが大切にすること: 分割エディトリアル（画像半分＋テキスト半分）。
                   由来: home V / カタログ SEC-N02（split50/50・左寄せ見出し）。overview S3（紺全面パネル引用主役型）
                   とは別型（明背景の画像+テキスト分割・3つの提供価値を細罫の定義行で提示）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Our Principles</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  私たちが大切にすること
                </h2>
                <div className="grid lg:grid-cols-2 items-stretch border border-[#e2ddd2] overflow-hidden">
                  {/* 左: 画像フル */}
                  <div className="relative min-h-[280px] lg:min-h-[460px] bg-black order-1">
                    <img
                      src="/images/generated/jba-p-009-about-welcome-w3.webp"
                      alt="来客を静かに迎える執事の所作。先回りのおもてなしを体現する接遇の情景"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  {/* 右: テキスト列（リード＋3つの提供価値を細罫定義行で） */}
                  <div className="order-2 flex flex-col justify-center px-7 sm:px-10 lg:px-12 py-10 sm:py-12 lg:py-14 text-left">
                    <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                      私たちが提供するのは、人手の代替ではありません。段取りや雑事から解放され、本来向き合うべきことに集中できる環境そのものです。単なる家事の代行ではなく、暮らしの基盤を静かに整え、先回りで支える——執事という営みの本質を、次の3つの視点で大切にしています。
                    </p>
                    <dl className="border-t border-[#e2ddd2]">
                      {principles.map((p) => (
                        <div key={p.key} className="border-b border-[#e2ddd2] py-4">
                          <dt className="flex items-baseline gap-3 mb-1.5">
                            <span className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em]">{p.key}</span>
                            <span className="jba-display font-medium text-[#1a2942] text-lg leading-[1.4]">{p.title}</span>
                          </dt>
                          <dd className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </section>

              {/* --- IV. 次の一歩（紺CTAパネル型）。由来: overview S6 / home 締めCTAパネル。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  協会をもっと知る
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    全日本執事協会は、執事の育成と資格認定、実践的なセミナー、そして執事人材サービス「THE BUTLER」の運営を通じて、執事文化を社会に根づかせています。協会の理念や活動をひと通りご覧いただいたら、資格制度やサービスの一覧、ご相談の窓口もぜひお役立てください。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
                    <a href="/butler-services/" className={BTN08_ON_NAVY}>執事サービスを見る</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
